import AttachFileIcon from "@mui/icons-material/AttachFile";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  ListItemIcon,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Buffer } from "buffer";
import saveAs from "file-saver";
import { MRT_ColumnFiltersState, MRT_PaginationState, MRT_SortingState } from "material-react-table";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import CustomToast from "src/Components/CustomToast";
import ReactTable, { ColDef } from "src/Components/ReactTable/ReactTable";
import { TicketDetails, TicketFollowUpType } from "src/entity/recon-entity/ReconInterfaces";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { isOverFlowing } from "src/Utils/Common/CheckTextOverflow";
import { filterDateBetweenStartAndEndDate, formatDate, formatDateTime24Hours } from "src/Utils/DateUtils";
import { uiLoggerNamesWorkflow } from "src/Utils/Recon/UiLogger/Constants";
import uiLogger from "src/Utils/UiLogger";
import { DEFAULT_BRANCH_CODE, DEFAULT_COMPANY_ID } from "../PartnerCommunication/CommonLegacy/CommonComponents";
import { NdButton } from "../PartnerCommunication/MsmePartnerPortal/CommonComponents";
import { Recon360Context } from "../Recon360";
import { TabPanel } from "../ReconSettings/ReconSettings";
import BulkTasksModal from "./BulkTasksModal";
import { TICKET_PRIORITIES } from "./CreateTicket";
import { TicketStatuses } from "./CurrentTicket";
import DiscardTicketModal from "./DiscardTicketModal";
import EditTaskModal from "./EditTaskModal";
import SaveTicketModal from "./SaveTicketModal";
import TaskAttachmentsModal from "./TaskAttachmentsModal";
import { NAKAD_SERVICE } from "./TasksHistoryModal";
import "./ViewTickets.scss";
import ViewTicketsFilter, { TicketListFilter } from "./ViewTicketsFilter";

export const renderGroupedOptions = (taskCategories, allStatuses) => {
  return Object.values(taskCategories).map((category) => {
    if (category === TicketStatuses.Saved || category === TicketStatuses.Discarded) return null;
    return [
      <ListSubheader
        disableSticky
        key={category as string}
        sx={{
          borderTop: "1px solid rgba(0,0,0,.12)",
          fontWeight: 700,
          fontSize: "16px",
          color: "rgba(0, 0, 0, 0.87)",
        }}
      >
        {category as string}
      </ListSubheader>,
      ...allStatuses
        .filter((ticket) => ticket.category === category)
        .map((item) => (
          <MenuItem
            key={item.id}
            value={item.id}
            sx={{
              color:
                (item.status === TicketStatuses.IssueInLedger && "#EF6C00") ||
                (item.status === TicketStatuses.CheckerRejected && "#EF6C00") ||
                (item.status === TicketStatuses.ApproverRejected && "#EF6C00") ||
                (item.status === TicketStatuses.TechnicalIssue && "#EF6C00"),
            }}
          >
            {item.status}
          </MenuItem>
        )),
    ];
  });
};

const filter = createFilterOptions<any>();

export enum BackendTicketStatuses {
  Saved = "Saved Ticket",
  Discarded = "Discarded Ticket",
  Cleared = "Cleared Ticket",
}
export enum UITicketStatuses {
  Save = "Save Ticket",
  Discard = "Discard Ticket",
  Clear = "Clear Ticket",
}
export enum TicketMetaStatuses {
  Saved = "Saved",
  Discarded = "Discarded",
  Cleared = "Cleared",
  InProgress = "In Progress",
}
enum TicketViewType {
  All = "All",
  Ongoing = "Ongoing",
  Previous = "Previous",
  Discarded = "Discarded",
}
export const mappingForUiLogStatuses = {
  "Both Side Ledger Pending": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_BOTH_SIDE_LEDGER_PENDING_CLICK,
  "Partner Ledger Pending": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_PARTNER_LEDGER_PENDING_CLICK,
  "Own Ledger Pending": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_OWN_LEDGER_PENDING_CLICK,
  "Issue in Ledger": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_ISSUE_IN_LEDGER_CLICK,
  "Yet to Start": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_YET_TO_START_CLICK,
  "Recon in Progress": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_RECON_IN_PROGRESS_CLICK,
  "Maker Passed": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_MAKER_PASSED_CLICK,
  "Checker Passed": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_CHECKER_PASSED_CLICK,
  "Checker Rejected": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_CHECKER_REJECTED_CLICK,
  "Sent to Approver": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_SENT_TO_APPROVER_CLICK,
  "Approver Passed": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_APPROVER_PASSED_CLICK,
  "Approver Rejected": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_APPROVER_REJECTED_CLICK,
  "Technical Issue": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_TECHNICAL_ISSUE_CLICK,
  Dropped: uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_DROPPED_CLICK,
  Saved: uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_SAVE_TICKET_CLICK,
  Discarded: uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_DISCARD_TICKET_CLICK,
  "Recon Sent to Partner": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_RECON_SENT_TO_PARTNER_CLICK,
  "Work in Progress- Action items": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_WORK_IN_PROGRESS_ACTION_ITEMS_CLICK,
  "Sign-off Pending": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_SIGNOFF_PENDING_WITH_PARTNER_CLICK,
  "Signed-off with partner": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_SIGNED_OFF_WITH_PARTNER_CLICK,
  "On-Hold": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_ON_HOLD_CLICK,
  "Recon Finished by user": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_RECON_FINISHED_BY_USER_CLICK,
  "Balance Confirmed": uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_BALANCE_CONFIRMED_CLICK,
};

// Tabs map to the backend view types; rows are paged/filtered/sorted on the server (ListTicketsPage)
const TAB_VIEWS = [TicketViewType.Ongoing, TicketViewType.Previous, TicketViewType.Discarded];
const DEFAULT_PAGINATION: MRT_PaginationState = { pageIndex: 0, pageSize: 100 };
const DATE_RANGE_COLUMNS = ["Due Date", "Creation Date", "Time of Last Status Change"];
const TASK_IDS_STORAGE_KEY = "bpIdsForWhichTasksAlreadyExists";

const toServerFilters = (columnFilters: MRT_ColumnFiltersState) =>
  columnFilters.map(({ id, value }) => {
    if (DATE_RANGE_COLUMNS.includes(id) && Array.isArray(value)) {
      return { id, value: value.map((date) => (date ? moment(date?.$d || date).format("YYYY-MM-DD") : null)) };
    }
    return { id, value };
  });

const readTaskIdsFromStorage = (): string[] => {
  try {
    const taskIds = JSON.parse(window.localStorage.getItem(TASK_IDS_STORAGE_KEY));
    return Array.isArray(taskIds) ? taskIds.map(String) : [];
  } catch {
    return [];
  }
};

export const ViewTickets = (props) => {
  const {
    companyId,
    branchCode,
    allTaskLabels,
    getAllTaskLabels,
    showSaveTicketModal,
    setShowSaveTicketModal,
    showDiscardTicketModal,
    setShowDiscardTicketModal,
    currentEditableBpId,
    storeAllCategories,
    currentSelectedTaskDetails,
  } = useContext(Recon360Context);
  const { actor } = useContext(userContext);

  const [tabValue, setTabValue] = React.useState(0);
  const [isFilterApplied, setIsFilterApplied] = React.useState(false);

  const [downloadingOngoingTasksReport, setDownloadingOngoingTasksReport] = React.useState(false);
  const [downloadingClearedTasksReport, setDownloadingClearedTasksReport] = React.useState(false);
  const [downloadingDiscardedTasksReports, setDownloadingDiscardedTasksReports] = React.useState(false);

  const editingBpId = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const currentTaskDetails = useRef<{ taskId: number; mappingId: number }>(null);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isUpdatingMetaStatus, setIsUpdatingMetaStatus] = useState<boolean>(false);

  const [showBulkTasksModal, setShowBulkTasksModal] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [clearRows, setClearRows] = useState(false);

  const [showTaskAttachmentsModal, setShowTaskAttachmentsModal] = useState<boolean>(false);

  const [showEditTaskModal, setShowEditTaskModal] = useState<boolean>(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState(null);

  const [tickets, setTickets] = useState<TicketDetails[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loadingTickets, setLoadingTickets] = useState<boolean>(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  // task ids handed over from "Move tasks" (partner communication) open the ongoing tab pre-filtered
  const [initialTaskIds] = useState<string[]>(readTaskIdsFromStorage);
  const [panelFilters, setPanelFilters] = useState<TicketListFilter[]>(
    initialTaskIds.length ? [{ id: "id", value: initialTaskIds }] : []
  );
  const categoriesLoaded = useRef(false);
  const latestRequest = useRef(0);

  useEffect(() => {
    window.localStorage.removeItem(TASK_IDS_STORAGE_KEY);
  }, []);

  const loadTickets = () => {
    const requestId = ++latestRequest.current;
    setLoadingTickets(true);
    useFetch(API_ENDPOINTS.LIST_TICKETS_PAGE.url, "POST", {
      failureMessage: API_ENDPOINTS.LIST_TICKETS_PAGE.failureMessage,
      data: {
        view: TAB_VIEWS[tabValue],
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        filters: [...toServerFilters(columnFilters), ...panelFilters],
        sorting: sorting.map(({ id, desc }) => ({ id, desc })),
        includeCategories: !categoriesLoaded.current,
      },
      thenCallBack: (response) => {
        if (requestId !== latestRequest.current) return; // a newer page/filter request is in flight
        if (response.data.allCategories) {
          storeAllCategories.current = response.data.allCategories;
          categoriesLoaded.current = true;
        }
        setTickets(response.data.tickets || []);
        setTotalCount(response.data.totalCount || 0);
        setLoadingTickets(false);
      },
      catchCallBack: () => {
        if (requestId === latestRequest.current) setLoadingTickets(false);
      },
    });
  };

  useEffect(() => {
    // debounce: column text filters change on every keystroke
    const timer = setTimeout(loadTickets, 400);
    return () => clearTimeout(timer);
  }, [tabValue, pagination, sorting, columnFilters, panelFilters, props.refreshKey]);

  const resetToFirstPage = () => setPagination((prev) => ({ ...prev, pageIndex: 0 }));

  const serverSideTableProps = {
    rows: tickets,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enablePagination: true,
    enableGlobalFilter: false,
    rowCount: totalCount,
    getRowId: (row: TicketDetails) => String(row.id),
    onPaginationChange: setPagination,
    onSortingChange: (updater) => {
      setSorting(updater);
      resetToFirstPage();
    },
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      resetToFirstPage();
    },
    additionalState: { pagination, sorting, columnFilters },
    internalActionsOrder: ["filterToggle", "showHideColumns", "fullScreenToggle"] as (
      | "filterToggle"
      | "showHideColumns"
      | "fullScreenToggle"
    )[],
  };

  const applyPanelFilters = (filters: TicketListFilter[]) => {
    setPanelFilters(filters);
    resetToFirstPage();
  };

  const downloadAccountTicketsReport = async (view: string) => {
    if (view === TicketViewType.Ongoing) setDownloadingOngoingTasksReport(true);
    else if (view === TicketViewType.Previous) setDownloadingClearedTasksReport(true);
    else if (view === TicketViewType.Discarded) setDownloadingDiscardedTasksReports(true);
    await useFetch(API_ENDPOINTS.GET_REPORT_FOR_ACCOUNT(view).url, "GET", {
      failureMessage: API_ENDPOINTS.GET_REPORT_FOR_ACCOUNT(view).failureMessage,
      thenCallBack: (response) => {
        const { excelBuffer, filename } = response.data;

        const excelData = Buffer.from(excelBuffer, "base64");
        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const blob = new Blob([excelData], { type: fileType });

        saveAs(blob, filename);
        if (view === TicketViewType.Ongoing) setDownloadingOngoingTasksReport(false);
        else if (view === TicketViewType.Previous) setDownloadingClearedTasksReport(false);
        else if (view === TicketViewType.Discarded) setDownloadingDiscardedTasksReports(false);
        toast.success(<CustomToast message="Report downloaded successfully" />);
      },
      catchCallBack: () => {
        if (view === TicketViewType.Ongoing) setDownloadingOngoingTasksReport(false);
        else if (view === TicketViewType.Previous) setDownloadingClearedTasksReport(false);
        else if (view === TicketViewType.Discarded) setDownloadingDiscardedTasksReports(false);
        toast.error(<CustomToast message="Error in downloading report" />);
      },
    });
  };

  const updateTaskMetaStatus = (status: string, taskDetails: { taskId: number; mappingId: number }) => {
    setIsUpdatingMetaStatus(true);
    uiLogger(mappingForUiLogStatuses[status].functionName, companyId.current, branchCode.current, {
      message: mappingForUiLogStatuses[status].functionName,
      businessPartnerId: editingBpId.current,
      ticketStatus: status,
      idOfTicket: taskDetails.taskId,
    });
    useFetch(API_ENDPOINTS.UPDATE_TICKET_META_STATUS.url, "POST", {
      failureMessage: API_ENDPOINTS.UPDATE_TICKET_META_STATUS.failureMessage,
      showSuccessToast: true,
      data: {
        ticketId: taskDetails.taskId,
        mappingId: taskDetails.mappingId,
        metaStatus: status,
      },
      thenCallBack: () => {
        props.fetchTickets();
        setShowSaveTicketModal(false);
        setShowDiscardTicketModal(false);
        setIsUpdatingMetaStatus(false);
      },
      catchCallBack: () => {
        props.fetchTickets();
        setShowSaveTicketModal(false);
        setShowDiscardTicketModal(false);
        setIsUpdatingMetaStatus(false);
      },
    });
  };

  const updateTicketStatus = (statusId: number, ticketId: number, statusName: string, mappingId: number) => {
    setIsStatusUpdating(true);
    try {
      const uiLogFunctionName =
        mappingForUiLogStatuses[statusName]?.functionName ||
        uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_CUSTOM_STATUS_CLICK.functionName;
      const uiLogMessage =
        mappingForUiLogStatuses[statusName]?.message ||
        uiLoggerNamesWorkflow.UI_WF_DB_RECON_STATUS_CUSTOM_STATUS_CLICK.message;
      uiLogger(uiLogFunctionName, companyId.current, branchCode.current, {
        message: uiLogMessage,
        businessPartnerId: editingBpId.current,
        ticketStatus: statusName,
        idOfTicket: ticketId,
        statusId,
      });
    } catch (error) {
      console.log(error);
    }

    useFetch(API_ENDPOINTS.UPDATE_TICKET_STATUS.url, "POST", {
      failureMessage: API_ENDPOINTS.UPDATE_TICKET_STATUS.failureMessage,
      data: {
        ticketId,
        mappingId,
        statusId,
      },
      thenCallBack: () => {
        props.fetchTickets();
        setIsStatusUpdating(false);
        toast.success(<CustomToast message="Successfully changed ticket status" />);
      },
      catchCallBack: (error) => {
        const message = error?.response?.data?.message || "Error in changing ticket status";
        props.fetchTickets();
        setIsStatusUpdating(false);
        toast.error(<CustomToast message={message} />);
      },
    });
  };

  // Updates Own and Bp Categories for each row on Blur
  const updateOwnAndBusinessPartnerMappingCategories = async (row, value: string[]) => {
    useFetch(API_ENDPOINTS.UPDATE_OWN_AND_BUSINESS_PARTNER_MAPPING_CATEGORIES.url, "POST", {
      failureMessage: API_ENDPOINTS.UPDATE_OWN_AND_BUSINESS_PARTNER_MAPPING_CATEGORIES.failureMessage,
      data: {
        ownId: actor.id,
        businessPartnerId: row.original.businessPartnerId,
        category: value,
        companyId: companyId.current || DEFAULT_COMPANY_ID,
        branchCode: branchCode.current || DEFAULT_BRANCH_CODE,
      },
      thenCallBack: () => {
        props.fetchTickets();
      },
    });
  };

  const userFilterOptions = ["-", ...(props.allUsers || [])];

  const columnDefinitionTickets = useMemo(
    (): ColDef<TicketDetails>[] => [
      {
        header: "Task ID",
        id: "id",
        sticky: "left",
        enablePinning: false,
        enableColumnDragging: false,
        enableResizing: false,
        accessorFn: (row) => row.id?.toString() || "",
        filterVariant: "text",
        Cell: ({ row }) => (
          <div style={{ textAlign: "center" }}>
            {row.original.metaStatus === TicketMetaStatuses.InProgress ? (
              <a
                onClick={() => {
                  const { businessPartnerId, businessPartner, id: ticketId, status } = row.original;
                  uiLogger(
                    uiLoggerNamesWorkflow.UI_WF_DB_PARTNER_SELECT_CLICK.functionName,
                    companyId.current,
                    branchCode.current,
                    {
                      message: uiLoggerNamesWorkflow.UI_WF_DB_PARTNER_SELECT_CLICK.message,
                      businessPartnerId: businessPartnerId,
                      idOfTicket: Number(ticketId),
                      ticketStatus: status,
                    }
                  );
                  const params = `bpId=${businessPartnerId}&bpName=${btoa(encodeURIComponent(businessPartner))}`;
                  const uri = `/${actor.name}/recon360/Summary/Ledger?${params}`;
                  window.open(uri, "_blank")?.focus();
                }}
                className="textOverflow_hidden_anchor"
              >
                {row.original.id}
              </a>
            ) : (
              <span>{row.original.id}</span>
            )}
          </div>
        ),
        size: 135,
      },
      {
        header: "Business Partner",
        id: "Business partner name",
        sticky: "left",
        enablePinning: false,
        filterVariant: "text",
        accessorFn: (row) => row.businessPartner || "",
        Cell: ({ row }) => (
          <div
            style={{ width: "inherit" }}
            className="textOverflow_hidden"
            ref={(th) => {
              if (th && isOverFlowing(th)) th.title = th.innerText;
            }}
          >
            {row.original.metaStatus === TicketMetaStatuses.InProgress ? (
              <a
                onClick={() => {
                  const { businessPartnerId, businessPartner, id: ticketId, status } = row.original;
                  uiLogger(
                    uiLoggerNamesWorkflow.UI_WF_DB_PARTNER_SELECT_CLICK.functionName,
                    companyId.current,
                    branchCode.current,
                    {
                      message: uiLoggerNamesWorkflow.UI_WF_DB_PARTNER_SELECT_CLICK.message,
                      businessPartnerId: businessPartnerId,
                      idOfTicket: Number(ticketId),
                      ticketStatus: status,
                    }
                  );
                  const params = `bpId=${businessPartnerId}&bpName=${btoa(encodeURIComponent(businessPartner))}`;
                  const uri = `/${actor.name}/recon360/Summary/Ledger?${params}`;
                  window.open(uri, "_blank")?.focus();
                }}
                className="textOverflow_hidden_anchor"
              >
                {row.original.businessPartner}
              </a>
            ) : (
              <span>{row.original.businessPartner}</span>
            )}
          </div>
        ),
        size: 250,
        minSize: 250,
      },
      {
        header: "Vendor Code",
        accessorFn: (row) => row?.bpVendorCode || "",
        enableSorting: true,
        Cell: ({ row }) => (
          <div
            className="textOverflow_hidden"
            ref={(th) => {
              if (th && isOverFlowing(th)) th.title = th.innerText;
            }}
            style={{ width: "inherit" }}
          >
            {row.original.bpVendorCode ? row.original.bpVendorCode : ""}
          </div>
        ),
        size: 200,
        filterVariant: "text",
      },
      {
        header: "Category",
        accessorFn: (row) => row.category?.join(", ") || "",
        size: 200,
        filterVariant: "multi-select",
        filterSelectOptions: storeAllCategories.current || [],
        Cell: ({ row }) => (
          <div className="category_ui" style={{ width: "100%" }}>
            <Autocomplete
              limitTags={1}
              value={row.original.category !== null && row.original.category?.length > 0 ? row.original.category : []}
              onChange={(_ev, val) => {
                let value = val as string[];
                if (value !== null && value?.length > 0) {
                  if (value[value.length - 1]?.includes("+Add => ")) {
                    value[value.length - 1] = value[value.length - 1].replace('+Add => "', "");
                    value[value.length - 1] = value[value.length - 1].replace('"', "");
                    const removeEmptyVal = value?.filter((el) => {
                      return el !== "";
                    });
                    value = removeEmptyVal;
                  }
                }
                storeAllCategories.current = [...new Set([...storeAllCategories.current, ...value])];
                updateOwnAndBusinessPartnerMappingCategories(row, value);
              }}
              noOptionsText={"Enter a new category"}
              multiple={true}
              id="tags-outlined"
              options={storeAllCategories?.current}
              getOptionLabel={(option) => option}
              filterSelectedOptions={true}
              filterOptions={(_, params) => {
                const filtered = filter(
                  storeAllCategories?.current === null ? [] : storeAllCategories?.current,
                  params
                );
                // Suggest the creation of a new value
                if (params.inputValue !== "") {
                  filtered.push(`+Add => "${params.inputValue.trim()}"`);
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  // placeholder="Add"
                  sx={{
                    "& input": {
                      px: "12px !important",
                    },
                  }}
                />
              )}
              sx={{
                "&, & div": {
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                },
                "& fieldset": {
                  borderWidth: "0.4px",
                  borderRadius: "12px",
                },
              }}
            />
          </div>
        ),
      },
      {
        header: "Status",
        id: "Status",
        filterVariant: "multi-select",
        filterSelectOptions: props.allStatuses.map((status) => status.status),
        accessorFn: (row) => row.status || "",
        Cell: ({ row, column }) => {
          const [anchorElDispute, setAnchorElDispute] = useState(null);
          const openDispute = Boolean(anchorElDispute);

          function handleClickDispute(event) {
            setAnchorElDispute(event.currentTarget);
          }
          function handleCloseDispute() {
            setAnchorElDispute(null);
          }
          const onChangeHandler = (e) => {
            const newValue = e.target.value;
            row.original.statusId = newValue;
            const statusName = props.allStatuses.find((status) => status.id === newValue).status;
            updateTicketStatus(newValue, row.original.id, statusName, row.original.mappingId);
          };
          return (
            <Select
              open={openDispute}
              onOpen={handleClickDispute}
              onClose={handleCloseDispute}
              disableUnderline={true}
              renderValue={() => row.original.status}
              displayEmpty={true}
              sx={{
                width: "inherit",
              }}
              disabled={
                column.getIsGrouped() ||
                row.original.metaStatus !== TicketMetaStatuses.InProgress ||
                selectedRow.length > 0
              }
              variant="standard"
              value={row.original.statusId}
              onChange={onChangeHandler}
              onFocus={() => {
                currentEditableBpId.current = row.original.businessPartnerId.toString();
                editingBpId.current = row.original.businessPartnerId;
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenu-list": {
                      paddingBottom: 0,
                      paddingTop: 0,
                      maxHeight: 450,
                    },
                  },
                },
              }}
            >
              {renderGroupedOptions(props.taskCategories, props.allStatuses)}

              <div
                style={{
                  position: "sticky",
                  bottom: "0",
                  backgroundColor: "#fff",
                  zIndex: 100,
                  display: "flex",
                  flexDirection: "column",
                  padding: "16px",
                  gap: "16px",
                  borderTop: "2px solid #e7e7e7",
                  marginTop: "8px",
                }}
              >
                <Button
                  variant="contained"
                  className="theme_btn fs_14 fw_500"
                  sx={{ borderRadius: "4px !important", padding: "7px 16px" }}
                  onClick={() => {
                    currentTaskDetails.current = { taskId: row.original.id, mappingId: row.original.mappingId };
                    setAnchorElDispute(null);
                    setShowSaveTicketModal(true);
                  }}
                >
                  SAVE/SIGN OFF TASK
                </Button>
                <Button
                  className="fs_14 fw_500"
                  variant="outlined"
                  color="error"
                  sx={{ borderRadius: "4px !important", padding: "6px 16px" }}
                  onClick={() => {
                    currentTaskDetails.current = { taskId: row.original.id, mappingId: row.original.mappingId };
                    setAnchorElDispute(null);
                    setShowDiscardTicketModal(true);
                  }}
                >
                  DISCARD TASK
                </Button>
              </div>
            </Select>
          );
        },
        size: 250,
        minSize: 250,
      },
      {
        header: "Task Label",
        id: "taskLabel",
        filterVariant: "multi-select",
        filterSelectOptions: ["-", ...(allTaskLabels || []).map((label) => label.title)],
        accessorFn: (row) => row.taskLabel || "-",
        filterFn: (row, _, filterValue) => {
          if (filterValue.length === 0) return true;
          return filterValue.includes(row.original.taskLabel ? row.original.taskLabel : "-");
        },
        Cell: ({ row }) => {
          return (
            <div
              className="textOverflow_hidden"
              ref={(th) => {
                if (th && isOverFlowing(th)) th.title = th.innerText;
              }}
              style={{ width: "inherit" }}
            >
              {row.original.taskLabel ? row.original.taskLabel : "-"}
            </div>
          );
        },
        size: 180,
        minSize: 180,
      },

      {
        header: "Maker",
        id: "Maker",
        filterVariant: "multi-select",
        filterSelectOptions: userFilterOptions,
        filterFn: (row, _, filterValue) => {
          if (filterValue.length === 0) return true;
          return filterValue.includes(row.original.maker ? row.original.maker : "-");
        },
        accessorFn: (row) => row.maker || "-",
        Cell: ({ row }) => {
          return (
            <div
              className="textOverflow_hidden"
              ref={(th) => {
                if (th && isOverFlowing(th)) th.title = th.innerText;
              }}
              style={{ width: "inherit" }}
            >
              {row.original.maker ? row.original.maker : "-"}
            </div>
          );
        },
        size: 180,
        minSize: 180,
      },
      {
        header: "Checker",
        id: "Checker",
        filterVariant: "multi-select",
        filterSelectOptions: userFilterOptions,
        filterFn: (row, _, filterValue) => {
          if (filterValue.length === 0) return true;
          return filterValue.includes(row.original.checker ? row.original.checker : "-");
        },
        accessorFn: (row) => row.checker || "-",
        Cell: ({ row }) => (
          <div
            className="textOverflow_hidden"
            ref={(th) => {
              if (th && isOverFlowing(th)) th.title = th.innerText;
            }}
            style={{ width: "inherit" }}
          >
            {row.original.checker ? row.original.checker : "-"}
          </div>
        ),
        size: 180,
        minSize: 180,
      },
      {
        header: "Approver",
        id: "Approver",
        filterVariant: "multi-select",
        filterSelectOptions: userFilterOptions,
        filterFn: (row, _, filterValue) => {
          if (filterValue.length === 0) return true;
          return filterValue.includes(row.original.approver ? row.original.approver : "-");
        },
        accessorFn: (row) => row.approver || "-",
        Cell: ({ row }) => (
          <div
            className="textOverflow_hidden"
            ref={(th) => {
              if (th && isOverFlowing(th)) th.title = th.innerText;
            }}
            style={{ width: "inherit" }}
          >
            {row.original.approver ? row.original.approver : "-"}
          </div>
        ),
        size: 190,
        minSize: 190,
      },
      {
        header: "Last User",
        id: "Last User",
        filterVariant: "multi-select",
        filterSelectOptions: userFilterOptions,
        accessorFn: (row) => row.lastActiveUser || "-",
        filterFn: (row, _, filterValue) => {
          if (filterValue.length === 0) return true;
          return filterValue.includes(row.original.lastActiveUser ? row.original.lastActiveUser : "-");
        },
        Cell: ({ row }) => (
          <div
            className="textOverflow_hidden"
            ref={(th) => {
              if (th && isOverFlowing(th)) th.title = th.innerText;
            }}
            style={{ width: "inherit" }}
          >
            {row.original.lastActiveUser ? row.original.lastActiveUser : "-"}
          </div>
        ),
        size: 190,
        minSize: 190,
      },
      {
        header: "Priority",
        id: "Priority",
        filterVariant: "multi-select",
        filterSelectOptions: TICKET_PRIORITIES,
        accessorFn: (row) => row.priority || "",
        Cell: ({ row }) => (
          <div className="textOverflow_hidden">{row.original.priority ? row.original.priority : "-"}</div>
        ),
        size: 180,
        minSize: 180,
      },

      {
        header: "Bucket",
        id: "Bucket",
        accessorFn: (row) => row.metaStatus || "",
        filterVariant: "multi-select",
        filterSelectOptions: Object.values(TicketMetaStatuses),
        Cell: ({ row }) => (
          <div className="textOverflow_hidden">{row.original.metaStatus ? row.original.metaStatus : "-"}</div>
        ),
        size: 180,
        minSize: 180,
      },
      {
        header: "Due Date",
        id: "Due Date",
        filterVariant: "date-range",
        filterFn: (row, _, filterValue) => {
          const startDate = filterValue[0] && filterValue[0].$d;
          const endDate = filterValue[1] && filterValue[1].$d;
          return filterDateBetweenStartAndEndDate(startDate, endDate, row.original.dueDate);
        },
        accessorFn: (row) => new Date(row.dueDate),
        Cell: ({ row }) => (
          <div className="textOverflow_hidden">{row.original.dueDate ? formatDate(row.original.dueDate) : "-"}</div>
        ),
        size: 350,
        minSize: 350,
      },
      {
        header: "Creation Date",
        id: "Creation Date",
        filterVariant: "date-range",
        filterFn: (row, _, filterValue) => {
          const startDate = filterValue[0] && filterValue[0].$d;
          const endDate = filterValue[1] && filterValue[1].$d;
          return filterDateBetweenStartAndEndDate(startDate, endDate, row.original.createdAt);
        },
        accessorFn: (row) => new Date(row.createdAt),
        Cell: ({ row }) => (
          <div className="textOverflow_hidden">{row.original.createdAt ? formatDate(row.original.createdAt) : "-"}</div>
        ),
        size: 350,
        minSize: 350,
      },
      {
        header: "Time of Last Status Change",
        id: "Time of Last Status Change",
        filterVariant: "date-range",
        filterFn: (row, _, filterValue) => {
          const startDate = filterValue[0] && filterValue[0].$d;
          const endDate = filterValue[1] && filterValue[1].$d;
          return filterDateBetweenStartAndEndDate(startDate, endDate, row.original.lastStatusChangeTime);
        },
        accessorFn: (row) => new Date(row.lastStatusChangeTime),
        // Render lastStatusChangeTime in the format "YYYY-MM-DD  HH:MM AM/PM"
        // AM/PM is compulsory
        Cell: ({ row }) => (
          <div className="textOverflow_hidden">
            {row.original.lastStatusChangeTime ? formatDateTime24Hours(row.original.lastStatusChangeTime) : "-"}
          </div>
        ),
        size: 350,
        minSize: 350,
      },
      {
        header: "Remarks",
        id: "remarks",
        accessorFn: (row) => row.remarks || "",
        filterFn: "contains",
        Cell: ({ row }) => (
          <div
            className="textOverflow_hidden"
            ref={(th) => {
              if (th && isOverFlowing(th)) th.title = th.innerText;
            }}
            style={{ width: "inherit" }}
          >
            {row.original.remarks ? row.original.remarks : "-"}
          </div>
        ),
        size: 250,
        minSize: 250,
      },
      {
        header: "Mail Follow Ups",
        id: "mailfollowups",
        filterVariant: "range",
        filterFn: "betweenInclusive",
        accessorFn: (row) => row.followUpCounts?.[TicketFollowUpType?.Mail] || 0,
        Cell: ({ row }) => (
          <Stack direction="row" columnGap={1} justifyContent="center">
            <span className="fs_16 fw_600">
              {(row.original.followUpCounts?.[TicketFollowUpType?.Mail] || "0") + " Times"}
            </span>
            <span>
              {row.original.followUpCounts?.[TicketFollowUpType?.Mail] !== 0 &&
                formatDate(row.original.followUpLastTime?.[TicketFollowUpType?.Mail])}
            </span>
          </Stack>
        ),
        size: 220,
        minSize: 220,
      },
      {
        header: "Conversation Follow Ups",
        id: "conversationfollowups",
        filterVariant: "range",
        filterFn: "betweenInclusive",
        accessorFn: (row) => row.followUpCounts?.[TicketFollowUpType?.Conversation] || 0,
        Cell: ({ row }) => (
          <Stack direction="row" columnGap={1} justifyContent="center">
            <span className="fs_16 fw_600">
              {(row.original.followUpCounts?.[TicketFollowUpType?.Conversation] || "0") + " Times"}
            </span>
            <span>
              {row.original.followUpCounts?.[TicketFollowUpType?.Conversation] !== 0 &&
                formatDate(row.original.followUpLastTime?.[TicketFollowUpType?.Conversation])}
            </span>
          </Stack>
        ),
        size: 300,
        minSize: 300,
      },
    ],

    [props.allStatuses, props.allUsersWithIds, props.allUsers, allTaskLabels, tickets, selectedRow]
  );

  return (
    <div className="view-tickets">
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={(e, val) => {
            e.preventDefault();
            setTabValue(val);
            setTickets([]);
            setTotalCount(0);
            setSorting([]);
            setColumnFilters([]);
            setPanelFilters([]);
            setPagination(DEFAULT_PAGINATION);
          }}
          aria-label="basic tabs example"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="ONGOING TASKS" sx={{ textTransform: "none" }} />
          <Tab label="PREVIOUS TASKS" sx={{ textTransform: "none" }} />
          <Tab label="DISCARDED TASKS" sx={{ textTransform: "none" }} />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0}>
        <ViewTicketsFilter
          onApply={applyPanelFilters}
          initialTaskIds={initialTaskIds}
          openFilter={isFilterApplied}
          setOpenFilter={setIsFilterApplied}
          allStatuses={props.allStatuses.map((status) => status.status) || []}
          allUsers={props.allUsers || []}
        />
        <Box sx={{ backgroundColor: "#fff", borderRadius: "20px", boxShadow: 5 }}>
          <Stack direction="row" p={2}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Ongoing Tasks
            </Typography>
            <Button
              onClick={() => setShowBulkTasksModal(true)}
              variant="outlined"
              sx={{
                fontSize: "15px",
                fontWeight: "500",
                ml: "auto",
                padding: "8px 22px",
                borderRadius: "4px !important",
                color: "rgba(84, 28, 76, 1)",
                borderColor: "rgba(84, 28, 76, 0.50)",
                "&:hover, &.Mui-focusVisible": { borderColor: "rgba(84, 28, 76, 1)" },
              }}
            >
              BULK TASKS
            </Button>
            <NdButton
              variant="contained"
              sx={{ ml: 2 }}
              onClick={() => {
                uiLogger(
                  uiLoggerNamesWorkflow.UI_WF_DB_DOWNLOAD_TASK_REPORT_CLICK.functionName,
                  companyId.current,
                  branchCode.current,
                  { message: uiLoggerNamesWorkflow.UI_WF_DB_DOWNLOAD_TASK_REPORT_CLICK.message }
                );
                downloadAccountTicketsReport(TicketViewType.Ongoing);
              }}
              disabled={downloadingOngoingTasksReport}
            >
              <LoadingIcon loading={downloadingOngoingTasksReport} />
              Download Report
            </NdButton>
            <NdButton
              variant="contained"
              sx={{ ml: 2 }}
              onClick={() => {
                uiLogger(
                  uiLoggerNamesWorkflow.UI_WF_DB_FILTER_CLICK.functionName,
                  companyId.current,
                  branchCode.current,
                  {
                    message: uiLoggerNamesWorkflow.UI_WF_DB_FILTER_CLICK.message,
                  }
                );
                setIsFilterApplied(true);
              }}
            >
              Filter
            </NdButton>
          </Stack>
          <ReactTable
            {...serverSideTableProps}
            loading={isStatusUpdating || loadingTickets}
            pageSelectMode={true}
            enableFacetedValues={true}
            enableRowVirtualization={true}
            columns={columnDefinitionTickets}
            enableAggregations={true}
            setSelectedRow={setSelectedRow}
            clearRowsSelection={clearRows}
            setClearRowsSelection={setClearRows}
            initialStateColumnVisibility={{ "Last User": false }}
            renderRowActionMenuItems={({ row, closeMenu }) => [
              <MenuItem
                key="editTask"
                disabled={row.original.metaStatus === TicketMetaStatuses.Saved}
                onClick={() => {
                  uiLogger(
                    uiLoggerNamesWorkflow.UI_WF_DB_ACTIONS_EDIT_CLICK.functionName,
                    companyId.current,
                    branchCode.current,
                    {
                      message: uiLoggerNamesWorkflow.UI_WF_DB_ACTIONS_EDIT_CLICK.message,
                      idOfTicket: row.original.id,
                    }
                  );
                  setShowEditTaskModal(true);
                  setSelectedRowForEdit(row.original);
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                Edit Task
              </MenuItem>,
              <MenuItem
                key="viewAttachments"
                onClick={() => {
                  currentSelectedTaskDetails.current = {
                    businessPartnerName: row.original.businessPartner,
                    taskId: row.original.id,
                    mappingId: row.original.mappingId,
                  };
                  setShowTaskAttachmentsModal(true);
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <AttachFileIcon />
                </ListItemIcon>
                View Attachments
              </MenuItem>,
              <MenuItem
                key={"viewHistory"}
                onClick={() => {
                  currentSelectedTaskDetails.current = {
                    businessPartnerName: row.original.businessPartner,
                    taskId: row.original.id,
                    mappingId: row.original.mappingId,
                  };
                  props.setShowTasksHistoryModal(true);
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <HistoryIcon />
                </ListItemIcon>
                View History
              </MenuItem>,
            ]}
            isSaving={isEditing}
          />
        </Box>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <ViewTicketsFilter
          onApply={applyPanelFilters}
          openFilter={isFilterApplied}
          setOpenFilter={setIsFilterApplied}
          allStatuses={props.allStatuses.map((status) => status.status) || []}
          allUsers={props.allUsers || []}
        />
        <Box sx={{ backgroundColor: "#fff", borderRadius: "20px", boxShadow: 5 }}>
          <Stack direction="row" p={2}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Previous Tasks
            </Typography>
            <NdButton
              variant="contained"
              sx={{ ml: "auto" }}
              onClick={() => {
                uiLogger(
                  uiLoggerNamesWorkflow.UI_WF_DB_DOWNLOAD_TASK_REPORT_CLICK.functionName,
                  companyId.current,
                  branchCode.current,
                  {
                    message: uiLoggerNamesWorkflow.UI_WF_DB_DOWNLOAD_TASK_REPORT_CLICK.message,
                  }
                );
                downloadAccountTicketsReport(TicketViewType.Previous);
              }}
              disabled={downloadingClearedTasksReport}
            >
              <LoadingIcon loading={downloadingClearedTasksReport} />
              Download Report
            </NdButton>
            <NdButton
              variant="contained"
              sx={{ ml: 2 }}
              onClick={() => {
                uiLogger(
                  uiLoggerNamesWorkflow.UI_WF_DB_FILTER_CLICK.functionName,
                  companyId.current,
                  branchCode.current,
                  {
                    message: uiLoggerNamesWorkflow.UI_WF_DB_FILTER_CLICK.functionName,
                  }
                );
                setIsFilterApplied(true);
              }}
            >
              Filter
            </NdButton>
          </Stack>

          <ReactTable
            {...serverSideTableProps}
            loading={loadingTickets}
            columns={columnDefinitionTickets}
            enableRowSelection={false}
            enableAggregations={true}
            enableRowVirtualization={true}
            enableFacetedValues={true}
            initialStateColumnVisibility={{ "Last User": false }}
            renderRowActionMenuItems={({ row, closeMenu }) => [
              <MenuItem
                key="viewAttachments"
                onClick={() => {
                  currentSelectedTaskDetails.current = {
                    businessPartnerName: row.original.businessPartner,
                    taskId: row.original.id,
                    mappingId: row.original.mappingId,
                  };
                  setShowTaskAttachmentsModal(true);
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <AttachFileIcon />
                </ListItemIcon>
                View Attachments
              </MenuItem>,
            ]}
          />
        </Box>
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <ViewTicketsFilter
          onApply={applyPanelFilters}
          openFilter={isFilterApplied}
          setOpenFilter={setIsFilterApplied}
          allStatuses={props.allStatuses.map((status) => status.status) || []}
          allUsers={props.allUsers || []}
        />
        <Box sx={{ backgroundColor: "#fff", borderRadius: "20px", boxShadow: 5 }}>
          <Stack direction="row" p={2}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Discarded Tasks
            </Typography>
            <NdButton
              variant="contained"
              sx={{ ml: "auto" }}
              onClick={() => {
                uiLogger(
                  uiLoggerNamesWorkflow.UI_WF_DB_DOWNLOAD_TASK_REPORT_CLICK.functionName,
                  companyId.current,
                  branchCode.current,
                  { message: uiLoggerNamesWorkflow.UI_WF_DB_DOWNLOAD_TASK_REPORT_CLICK.message }
                );
                downloadAccountTicketsReport(TicketViewType.Discarded);
              }}
              disabled={downloadingDiscardedTasksReports}
            >
              <LoadingIcon loading={downloadingDiscardedTasksReports} />
              Download Report
            </NdButton>
            <NdButton
              variant="contained"
              sx={{ ml: 2 }}
              onClick={() => {
                uiLogger(
                  uiLoggerNamesWorkflow.UI_WF_DB_FILTER_CLICK.functionName,
                  companyId.current,
                  branchCode.current,
                  { message: uiLoggerNamesWorkflow.UI_WF_DB_FILTER_CLICK.message }
                );
                setIsFilterApplied(true);
              }}
            >
              Filter
            </NdButton>
          </Stack>

          <ReactTable
            {...serverSideTableProps}
            loading={loadingTickets}
            columns={columnDefinitionTickets}
            enableRowSelection={false}
            enableAggregations={true}
            enableRowVirtualization={true}
            enableFacetedValues={true}
            initialStateColumnVisibility={{ "Last User": false }}
          />
        </Box>
      </TabPanel>
      {showSaveTicketModal && (
        <SaveTicketModal
          showSaveTicketModal={showSaveTicketModal}
          setShowSaveTicketModal={setShowSaveTicketModal}
          changeStatus={updateTaskMetaStatus}
          taskDetails={currentTaskDetails.current}
          isStatusUpdating={isUpdatingMetaStatus}
        />
      )}
      {showDiscardTicketModal && (
        <DiscardTicketModal
          showDiscardTicketModal={showDiscardTicketModal}
          setShowDiscardTicketModal={setShowDiscardTicketModal}
          changeStatus={updateTaskMetaStatus}
          taskDetails={currentTaskDetails.current}
          isStatusUpdating={isUpdatingMetaStatus}
        />
      )}
      <BulkTasksModal
        type="workflow"
        showBulkTasksModal={showBulkTasksModal}
        setShowBulkTasksModal={setShowBulkTasksModal}
        selectedTasks={selectedRow}
        allUsers={props.allUsersWithIds}
        allStatuses={props.allStatuses.map((status) => {
          return {
            id: status.id,
            name: status.status,
          };
        })}
        refetchData={props.fetchTickets}
        setClearRows={setClearRows}
        getAllTaskLabels={getAllTaskLabels}
        allTaskLabels={allTaskLabels}
        companyId={companyId.current}
        branchCode={branchCode.current}
      />
      {showTaskAttachmentsModal && (
        <TaskAttachmentsModal
          open={showTaskAttachmentsModal}
          setOpen={setShowTaskAttachmentsModal}
          businessPartnerName={currentSelectedTaskDetails.current.businessPartnerName}
          ticketId={currentSelectedTaskDetails.current.taskId}
          companyId={companyId.current}
          branchCode={branchCode.current}
          serviceType={NAKAD_SERVICE.Recon}
          mappingId={currentSelectedTaskDetails.current.mappingId}
        />
      )}
      {showEditTaskModal && (
        <EditTaskModal
          type="workflow"
          open={showEditTaskModal}
          setOpen={setShowEditTaskModal}
          allUsers={props.allUsersWithIds}
          row={selectedRowForEdit}
          fetchRowsData={props.fetchTickets}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          companyId={companyId.current}
          branchCode={branchCode.current}
          allTaskLabels={allTaskLabels}
          getAllTaskLabels={getAllTaskLabels}
        />
      )}
    </div>
  );
};
